import DateRangeIcon from '@mui/icons-material/DateRange'
import {
  Alert,
  Button as MuiButton,
  ButtonGroup,
  Chip,
  MenuItem,
  Select,
  useTheme
} from '@mui/material'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import CssBaseline from '@mui/material/CssBaseline'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import React from 'react'

import { BookingFormProps } from '../../types/ComponentProps'

/**
 * BookingForm - Presentation component for booking form UI
 *
 * Handles the form interface for selecting dates, duration, and repeat options
 * for desk bookings. Focuses solely on UI rendering and user interactions.
 */
export default function BookingForm({
  selectedFromDate,
  selectedRecurAmount,
  selectedRecurUnit,
  selectedWeekDays,
  selectedOffice,
  offices,
  busy: _busy,
  notifyUser,
  onFromDateChange,
  onAmountChange,
  onUnitChange,
  onWeekDaysChange,
  onOfficeChange,
  onNotificationClose
}: BookingFormProps) {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  const isWeekly = selectedRecurUnit === 'Same days every week'

  const weekDays = [
    { value: 0, label: 'Sun' },
    { value: 1, label: 'Mon' },
    { value: 2, label: 'Tue' },
    { value: 3, label: 'Wed' },
    { value: 4, label: 'Thu' },
    { value: 5, label: 'Fri' },
    { value: 6, label: 'Sat' }
  ]

  // Calculate day of week from selected date
  const getDayOfWeek = () => {
    if (!selectedFromDate) return ''
    const date = new Date(selectedFromDate)
    return date.toLocaleDateString('en-US', { weekday: 'long' })
  }

  const handleWeekDayToggle = (day: number) => {
    if (selectedWeekDays.includes(day)) {
      onWeekDaysChange(selectedWeekDays.filter(d => d !== day))
    } else {
      onWeekDaysChange([...selectedWeekDays, day].sort())
    }
  }

  const officeOptions = () => {
    return (
      offices?.map(office => (
        <MenuItem value={office.oid} key={office.oid}>
          {office.name}
        </MenuItem>
      )) || []
    )
  }

  const renderDurationButtons = () => {
    return (
      <ButtonGroup variant='outlined' size='small' sx={{ height: 32 }}>
        {[1, 2, 3, 4, 5].map(days => (
          <MuiButton
            key={days}
            onClick={() => onAmountChange(days)}
            variant={selectedRecurAmount === days ? 'contained' : 'outlined'}
            sx={{
              minWidth: 40,
              fontSize: '0.75rem',
              fontWeight: 500,
              px: 1
            }}
          >
            {days}d
          </MuiButton>
        ))}
      </ButtonGroup>
    )
  }

  const renderWeekDaySelector = () => {
    return (
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {weekDays.map(day => (
          <Chip
            key={day.value}
            label={day.label}
            onClick={() => handleWeekDayToggle(day.value)}
            color={selectedWeekDays.includes(day.value) ? 'primary' : 'default'}
            variant={
              selectedWeekDays.includes(day.value) ? 'filled' : 'outlined'
            }
            sx={{
              cursor: 'pointer',
              minWidth: 48,
              height: 32,
              fontSize: '0.75rem',
              fontWeight: 500
            }}
          />
        ))}
      </Box>
    )
  }

  const renderRepeatButtons = () => {
    const options = ['Once off', 'Same days every week']
    return (
      <ButtonGroup variant='outlined' size='small' sx={{ height: 32 }}>
        {options.map(option => (
          <MuiButton
            key={option}
            onClick={() => onUnitChange({ target: { value: option } } as any)}
            variant={selectedRecurUnit === option ? 'contained' : 'outlined'}
            sx={{
              fontSize: '0.75rem',
              fontWeight: 500,
              px: 2,
              textTransform: 'none'
            }}
          >
            {option === 'Once off' ? 'Once' : 'Weekly'}
          </MuiButton>
        ))}
      </ButtonGroup>
    )
  }

  return (
    <Container component='main' maxWidth='lg'>
      <CssBaseline />
      <Box
        sx={{
          marginTop: 2,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
            <DateRangeIcon fontSize='small' />
          </Avatar>
          <Typography component='h1' variant='h6' sx={{ fontWeight: 600 }}>
            Book a desk
          </Typography>
        </Box>

        <Typography variant='body1' color='text.secondary' sx={{ mb: 3 }}>
          Select a date, duration, and office to book your desk
        </Typography>

        <Box component='form' noValidate sx={{ width: '100%', maxWidth: 1000 }}>
          {/* Horizontal Flow for Main Controls */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'end',
              flexWrap: 'wrap',
              justifyContent: 'center',
              mb: 3,
              p: 3,
              backgroundColor: isDark
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(0, 0, 0, 0.03)',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Box sx={{ minWidth: 140 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 0.5
                }}
              >
                <Typography
                  variant='caption'
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: 'text.primary'
                  }}
                >
                  From
                </Typography>
                <Typography
                  variant='caption'
                  sx={{
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    color: 'text.secondary'
                  }}
                >
                  {getDayOfWeek()}
                </Typography>
              </Box>
              <TextField
                type='date'
                size='small'
                onChange={onFromDateChange}
                value={selectedFromDate}
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: '0.875rem',
                    py: 1
                  }
                }}
              />
            </Box>

            <Box>
              <Typography
                variant='caption'
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  mb: 0.5,
                  display: 'block',
                  color: 'text.primary'
                }}
              >
                {isWeekly ? 'Days of week' : 'Duration'}
              </Typography>
              {isWeekly ? renderWeekDaySelector() : renderDurationButtons()}
            </Box>

            <Box>
              <Typography
                variant='caption'
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  mb: 0.5,
                  display: 'block',
                  color: 'text.primary'
                }}
              >
                Repeat
              </Typography>
              {renderRepeatButtons()}
            </Box>

            <Box sx={{ minWidth: 220, maxWidth: 300 }}>
              <Typography
                variant='caption'
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  mb: 0.5,
                  display: 'block',
                  color: 'text.primary'
                }}
              >
                Office
              </Typography>
              <Select
                labelId='office-select-label'
                id='office-select'
                fullWidth={true}
                onChange={onOfficeChange}
                value={selectedOffice?.oid || ''}
                size='small'
              >
                {officeOptions()}
              </Select>
            </Box>
          </Box>
        </Box>

        <Box sx={{ mt: 1, width: '100%', maxWidth: 1000 }}>
          {notifyUser && (
            <Alert severity='success' onClose={onNotificationClose}>
              {notifyUser}
            </Alert>
          )}
        </Box>
      </Box>
    </Container>
  )
}
